// The move doors as the Fleet card calls them (#6771, epic #6552): `POST
// /v1/runners/:id/moves/preview` and `POST /v1/runners/:id/moves` (#6768, #6769). The
// server is the only judge of a move: the preview answers, for every other machine of the
// org, what it would say, and the card renders that answer and nothing it computed itself.
//
// BOTH DOORS ARE DESCRIPTORS (`./api-call.ts`), so the request the card sends is the one a
// copied command would send. The move carries a fresh `Idempotency-Key` per send (the route
// answers 400 without one); the MCP twin requires one, so its arguments carry only the
// placeholder the REST snippet prints, never the minted key.
//
// THE PREVIEW IS PARSED (`apiFetch`'s opt-in schema): a body this bundle cannot read is a
// failed preview, which starts nothing, rather than a half-read panel. Every field the
// server may add later is optional or passed through; a refusal `reason` this bundle does
// not know fails the parse, because a sentence cannot be rendered for it.
//
// A REFUSED MOVE is read from its problem body's extension members (`reason`, `side`,
// `machine_id`, `item`, `left`, `needs`, `free_at`, `room`; `moveRefusalWire`, `@developerz/mcp`).
// It is never handed to the card controls' shared failure handler, which would turn a 429
// into a page lock (`./card-controls.ts` `failed`): a move's 429 renders inline.

import {
  AgentModeKeySchema,
  MOVE_REFUSAL_REASONS,
  type MoveRefusalReason,
  SLOT_REFUSALS,
} from '@developerz/domain';
import { z } from 'zod';
import { ApiError, apiFetch } from './api';
import { type ApiCall, IDEMPOTENCY_KEY_PLACEHOLDER, sendApiCall } from './api-call';

/** One item a move names: a job, an epic (its plan id) or a capability group (its mode key). */
export const MoveItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('task'), id: z.string().min(1) }),
  z.object({ kind: z.literal('epic'), id: z.string().min(1) }),
  z.object({ kind: z.literal('group'), id: AgentModeKeySchema }),
]);
export type MoveItem = z.infer<typeof MoveItemSchema>;

const RoomSchema = z.object({
  check: z.enum(['slots', 'not_accepting']),
  refusal: z.enum(SLOT_REFUSALS).nullable(),
  occupancy: z.number(),
  cap: z.number(),
  needs: z.number(),
});

/** A refusal's fields, as both the preview and a refused move's problem body carry them. */
export const MoveRefusalWireSchema = z.object({
  reason: z.enum(MOVE_REFUSAL_REASONS),
  side: z.enum(['request', 'source', 'target', 'org']),
  machine_id: z.string().optional(),
  item: z.string().optional(),
  left: z.number().optional(),
  needs: z.number().optional(),
  free_at: z.string().nullable().optional(),
  room: RoomSchema.optional(),
});
export type MoveRefusalWire = {
  readonly reason: MoveRefusalReason;
  readonly side: 'request' | 'source' | 'target' | 'org';
  readonly machine_id?: string | undefined;
  readonly item?: string | undefined;
  readonly left?: number | undefined;
  readonly needs?: number | undefined;
  readonly free_at?: string | null | undefined;
  readonly room?:
    | {
        readonly check: 'slots' | 'not_accepting';
        readonly refusal: string | null;
        readonly occupancy: number;
        readonly cap: number;
        readonly needs: number;
      }
    | undefined;
};

/** A projection from real readings: `cpu` busy percent, `memory` FREE BYTES; null when unknown. */
const ProjectionSchema = z.object({ before: z.number(), after: z.number() }).nullable();
export type MoveProjection = { readonly before: number; readonly after: number } | null;

export const MovePreviewSchema = z.object({
  cost: z.number(),
  source: z.object({
    left: z.number(),
    free_at: z.string().nullable(),
    refusal: MoveRefusalWireSchema.optional(),
    cpu: ProjectionSchema,
  }),
  targets: z.array(
    z.object({
      runner_id: z.string(),
      refusal: MoveRefusalWireSchema.optional(),
      left_after: z.number(),
      cpu: ProjectionSchema,
      memory: ProjectionSchema,
    }),
  ),
});
export type MovePreview = z.infer<typeof MovePreviewSchema>;
export type MovePreviewTarget = MovePreview['targets'][number];

/** The move's 201: its batch and both machines' budgets after it. */
export type MoveAccepted = {
  readonly batch: {
    readonly id: string;
    readonly from_runner_id: string;
    readonly to_runner_id: string;
    readonly cost: number;
    readonly items: readonly { readonly task_id: string; readonly state: string }[];
  };
  /**
   * Each machine's budget after this move (`boxMoveWire`). Read as optional: the body is not
   * parsed, and a 201 without it still moved the work (#6772 lays it over the rows when sent).
   */
  readonly budget?: {
    readonly source: { readonly left: number; readonly free_at: string | null };
    readonly target: { readonly left: number; readonly free_at: string | null };
  };
};

/** The preview as one value. Writes nothing and spends nothing on the server. */
export function boxMovePreviewCall(sourceId: string, items: readonly MoveItem[]): ApiCall {
  return {
    capability: 'box.move_preview',
    method: 'POST',
    path: `/v1/runners/${encodeURIComponent(sourceId)}/moves/preview`,
    body: { items },
    mcp: { kind: 'verb', verb: 'box.move_preview', arguments: { box_id: sourceId, items } },
  };
}

/**
 * The move as one value; `idempotencyKey` is minted per send by the caller. `undoOf` makes it
 * an Undo (#6772): the batch it reverses, sent as the body's and the MCP twin's `undo_of`, the
 * one thing that turns the request into the reversal `undoVerdict` checks. Without it the same
 * items and machines are an ordinary move.
 */
export function boxMoveCall(
  sourceId: string,
  items: readonly MoveItem[],
  targetId: string,
  idempotencyKey: string,
  undoOf?: string,
): ApiCall {
  const undo = undoOf === undefined ? {} : { undo_of: undoOf };
  return {
    capability: 'box.move',
    method: 'POST',
    path: `/v1/runners/${encodeURIComponent(sourceId)}/moves`,
    body: { items, target_runner_id: targetId, ...undo },
    // The MCP `box move` REQUIRES `idempotency_key` (the REST door takes it as a header):
    // the copied call carries the same placeholder the REST snippet prints, never the key
    // this send minted, so a pasted command is complete and never replays this move.
    mcp: {
      kind: 'verb',
      verb: 'box.move',
      arguments: {
        box_id: sourceId,
        target_box_id: targetId,
        items,
        ...undo,
        idempotency_key: IDEMPOTENCY_KEY_PLACEHOLDER,
      },
    },
    idempotencyKey,
  };
}

/** Ask what every other machine would say. Rejects on a non-2xx or an unreadable body. */
export function previewMove(
  sourceId: string,
  items: readonly MoveItem[],
  token: string | null,
): Promise<MovePreview> {
  const call = boxMovePreviewCall(sourceId, items);
  return apiFetch(
    call.path,
    token,
    { method: call.method, body: JSON.stringify(call.body) },
    { schema: MovePreviewSchema },
  );
}

/** Send the move. Rejects with the problem on a refusal ({@link readMoveRefusal}). */
export function sendMove(
  sourceId: string,
  items: readonly MoveItem[],
  targetId: string,
  idempotencyKey: string,
  token: string | null,
  undoOf?: string,
): Promise<MoveAccepted> {
  return sendApiCall<MoveAccepted>(
    boxMoveCall(sourceId, items, targetId, idempotencyKey, undoOf),
    token,
  );
}

/**
 * A refused move's fields, read off its problem body, or null when the failure is not a
 * refusal this bundle can render (a 5xx, the network, a reason it does not know).
 */
export function readMoveRefusal(err: unknown): MoveRefusalWire | null {
  if (!(err instanceof ApiError) || (err.status !== 409 && err.status !== 429)) return null;
  const parsed = MoveRefusalWireSchema.safeParse(err.extensions ?? {});
  return parsed.success ? parsed.data : null;
}

/** The four 429s: a wait, rendered inline on its move; never a page lock. */
export const MOVE_WAIT_REASONS: ReadonlySet<MoveRefusalReason> = new Set([
  'cooling_down',
  'source_budget',
  'target_budget',
  'transfers_in_flight',
]);
